import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { track } from '../utils/analytics';

const SECTORS = ['Agriculture & Agroforestry', 'Renewable Energy', 'Cement', 'Aluminium', 'Iron & Steel', 'Chlor-Alkali', 'Pulp & Paper', 'Petroleum Refinery', 'Petrochemicals', 'Fertilisers', 'Textiles', 'Waste Management', 'Forestry & Land Use', 'Finance & Investment', 'Other'];

const PERSONAS = [
  {
    value: 'Individual',
    label: 'Individual / Retail',
    description: 'Climate-curious individual exploring the carbon market.',
    role: 'individual',
  },
  {
    value: 'SME / Startup',
    label: 'SME / Startup',
    description: 'Small or medium enterprise exploring offset opportunities or voluntary credits.',
    role: 'organization',
  },
  {
    value: 'Corporate',
    label: 'Corporate / Sustainability Team',
    description: 'Corporate entity tracking CCTS exposure or buying voluntary offsets.',
    role: 'organization',
  },
  {
    value: 'Project Developer / NGO',
    label: 'Project Developer / NGO',
    description: 'Organisation developing emission-reduction projects seeking credit issuance.',
    role: 'organization',
  },
  {
    value: 'Investor / Analyst',
    label: 'Investor / Analyst',
    description: 'Following market signals, pricing, and CCTS policy for investment analysis.',
    role: 'individual',
  },
  {
    value: 'Compliance Entity',
    label: 'Compliance Entity (Obligated)',
    description: 'Entity covered under CCTS sectoral targets with mandatory obligations.',
    role: 'compliance_entity',
  },
];

export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '', pan: '', gstin: '', entity_type: 'Individual', sector: '', address: '', role: 'individual', persona: PERSONAS[0] });
  const [errors, setErrors] = useState({});

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validateStep1 = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Full name required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = 'Valid email required';
    if (form.password.length < 8) nextErrors.password = 'Min 8 characters';
    if (!/[A-Z]/.test(form.password)) nextErrors.password = 'Must include uppercase letter';
    if (!/[0-9]/.test(form.password)) nextErrors.password = 'Must include number';
    if (form.password !== form.confirmPassword) nextErrors.confirmPassword = 'Passwords do not match';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep2 = () => {
    const nextErrors = {};
    if (!/^[6-9]\d{9}$/.test(form.phone)) nextErrors.phone = 'Valid 10-digit Indian mobile required';
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan.toUpperCase())) nextErrors.pan = 'Valid PAN required (e.g. ABCDE1234F)';
    if (!form.sector) nextErrors.sector = 'Sector required';
    if (!form.address.trim()) nextErrors.address = 'Address required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setLoading(true);
    try {
      await api.post('/auth/signup', { ...form, pan: form.pan.toUpperCase() });
      track('signup', { persona: form.entity_type, sector: form.sector });
      toast.success('Registration successful! KYC verification pending.');
      navigate('/login');
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        const nextErrors = {};
        apiErrors.forEach((item) => {
          nextErrors[item.path] = item.msg;
        });
        setErrors(nextErrors);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = (() => {
    if (!form.password) return '';
    if (form.password.length >= 8 && /[A-Z]/.test(form.password) && /[0-9]/.test(form.password) && /[^A-Za-z0-9]/.test(form.password)) return 'Strong';
    if (form.password.length >= 8 && /[A-Z]/.test(form.password) && /[0-9]/.test(form.password)) return 'Good';
    if (form.password.length >= 6) return 'Weak';
    return '';
  })();

  const passwordColor = passwordStrength === 'Strong'
    ? 'var(--neon-green)'
    : passwordStrength === 'Good'
      ? 'var(--neon-yellow)'
      : 'var(--neon-pink)';

  return (
    <div className="auth-shell">
      <div className="auth-grid">
        <div className="surface-panel glow-surface auth-side grid-bg">
          <div>
            <Link to="/" className="auth-brand">
              <div className="brand-mark" />
              <div className="brand-copy">
                <div className="brand-title">CarbonX</div>
                <div className="brand-subtitle">Premium carbon workflow</div>
              </div>
            </Link>

            <h1 className="auth-title">
              Create a trading account without the usual friction.
            </h1>
            <p className="auth-copy">
              The onboarding surface is now simpler and more product-grade. Step one covers the account. Step two captures the core KYC metadata needed before verification.
            </p>
          </div>

          <div className="auth-feature-grid">
            <div className="auth-feature">
              <div className="stat-label">Step 1</div>
              <div className="feature-title">Account setup</div>
              <div className="stat-sub">Identity, email, account type, and password.</div>
            </div>
            <div className="auth-feature">
              <div className="stat-label">Step 2</div>
              <div className="feature-title">KYC basics</div>
              <div className="stat-sub">Phone, PAN, sector, and registered address.</div>
            </div>
            <div className="auth-feature">
              <div className="stat-label">After signup</div>
              <div className="feature-title">Verification review</div>
              <div className="stat-sub">Trading remains gated until approval is completed.</div>
            </div>
            <div className="auth-feature">
              <div className="stat-label">Future mobile</div>
              <div className="feature-title">Shared experience system</div>
              <div className="stat-sub">This structure will translate cleanly into Android and iOS flows.</div>
            </div>
          </div>
        </div>

        <div className="surface-panel auth-panel">
          <div className="stepper-shell">
            <div className={`step-node ${step === 1 ? 'active' : ''}`} onClick={() => setStep(1)} style={{ cursor: 'pointer' }}>
              <div className="step-title">Step 1</div>
              <div className="step-copy">Account details</div>
            </div>
            <div className={`step-node ${step === 2 ? 'active' : ''}`} onClick={() => validateStep1() && setStep(2)} style={{ cursor: 'pointer' }}>
              <div className="step-title">Step 2</div>
              <div className="step-copy">KYC information</div>
            </div>
          </div>

          <div className="auth-header">
            <div className="section-tag">Create account</div>
            <h2 className="auth-title-small">{step === 1 ? 'Start with the essentials' : 'Complete your KYC basics'}</h2>
            <p className="auth-copy-small">
              {step === 1
                ? 'Choose the account type and create a password that meets the current policy checks.'
                : 'Add the core information needed for identity review and market participation.'}
            </p>
          </div>

          {step === 1 && (
            <div>
              <div className="form-group">
                <label className="form-label">Full name</label>
                <input className="form-input" type="text" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Rahul Kumar Sharma" />
                {errors.name && <div className="form-error">{errors.name}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Email address</label>
                <input className="form-input" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="rahul@example.com" />
                {errors.email && <div className="form-error">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Who are you?</label>
                <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.3rem' }}>
                  {PERSONAS.map((persona) => {
                    const selected = form.entity_type === persona.value;
                    return (
                      <div
                        key={persona.value}
                        onClick={() => {
                          update('entity_type', persona.value);
                          update('role', persona.role);
                          setForm((prev) => ({ ...prev, entity_type: persona.value, role: persona.role, persona }));
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          border: `1px solid ${selected ? 'var(--neon-cyan)' : 'var(--border2)'}`,
                          background: selected ? 'rgba(0,229,255,0.06)' : 'var(--surface2)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s',
                        }}
                      >
                        <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: selected ? 'var(--neon-cyan)' : 'var(--text)' }}>{persona.label}</div>
                        <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.62rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{persona.description}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" value={form.password} onChange={(e) => update('password', e.target.value)} placeholder="Minimum 8 characters" />
                {passwordStrength && (
                  <div className="stat-label" style={{ color: passwordColor }}>Strength: {passwordStrength}</div>
                )}
                {errors.password && <div className="form-error">{errors.password}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Confirm password</label>
                <input className="form-input" type="password" value={form.confirmPassword} onChange={(e) => update('confirmPassword', e.target.value)} placeholder="Repeat password" />
                {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
              </div>

              <button type="button" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => validateStep1() && setStep(2)}>
                Continue to KYC
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="form-group">
                <label className="form-label">Mobile number</label>
                <input className="form-input" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="9876543210" />
                {errors.phone && <div className="form-error">{errors.phone}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">PAN number</label>
                <input className="form-input" type="text" value={form.pan} onChange={(e) => update('pan', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
                {errors.pan && <div className="form-error">{errors.pan}</div>}
              </div>

              {form.entity_type !== 'Individual' && (
                <div className="form-group">
                  <label className="form-label">GSTIN</label>
                  <input className="form-input" type="text" value={form.gstin} onChange={(e) => update('gstin', e.target.value.toUpperCase())} placeholder="27XXXXX1234X1ZX" maxLength={15} />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Sector</label>
                <select className="form-select" value={form.sector} onChange={(e) => update('sector', e.target.value)}>
                  <option value="">Select your sector</option>
                  {SECTORS.map((sector) => <option key={sector}>{sector}</option>)}
                </select>
                {errors.sector && <div className="form-error">{errors.sector}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Registered address</label>
                <textarea className="form-textarea" value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="Full address including city, state, and PIN" rows={3} />
                {errors.address && <div className="form-error">{errors.address}</div>}
              </div>

              <div className="card" style={{ background: 'rgba(255,238,0,0.05)', borderColor: 'rgba(255,238,0,0.16)', marginBottom: '1rem' }}>
                <div className="stat-label" style={{ color: 'var(--neon-yellow)' }}>KYC note</div>
                <div className="stat-sub" style={{ color: '#ffe873' }}>
                  Trading stays locked until verification is approved. This form captures the account metadata only; document review still follows after signup.
                </div>
              </div>

              <div className="inline-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: '1.5rem' }} className="stat-sub">
            Already registered? <Link to="/login" style={{ color: 'var(--neon-cyan)', textDecoration: 'none' }}>Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
